import ProjectSectionType from 'src/enums/companyProjects/ProjectSectionType';

export const ProjectEndpoints = {
  projectContainerEmail: {
    containerSend: '/api/ProjectContainerEmail/container/send',
    inboxSend: '/api/ProjectContainerEmail/inbox/send',
    recipientOptions: (containerId: number) => `/api/ProjectContainerEmail/container/${containerId}/recipientOptions`
  },
  /** Groups-facing document template CRUD (auth-ready via ProjectContractTemplateManageAccess). */
  documentTemplates: {
    basicCrud: 'api/document-templates',
    importPreview: 'api/document-templates/import-preview',
    previewPdf: 'api/document-templates/preview-pdf',
    mergeTokens: 'api/document-templates/merge-tokens'
  },
  projectContainers: {
    create: '/api/project-containers',
    update: (id: number) => `/api/project-containers/${id}`,
    edit: (id: number) => `/api/project-containers/${id}/edit`,
    exportToExcel: '/api/project-containers/export-to-excel',
    companyOverview: (id: number) => `/api/project-containers/${id}/company/overview`,
    companyAssessmentContext: (id: number) => `/api/project-containers/${id}/company/assessment-context`,
    companyPolicies: (id: number) => `/api/project-containers/${id}/company/policies`,
    companyContacts: (id: number) => `/api/project-containers/${id}/company/contacts`,
    companyTravellerOptions: (id: number) => `/api/project-containers/${id}/company/contacts/traveller-options`,
    companyContactSelect: (id: number) => `/api/project-containers/${id}/company/contacts/select`,
    companyApproverContact: (id: number) => `/api/project-containers/${id}/company/contacts/approver`,
    companyContactEmail: (id: number, contactId: number) =>
      `/api/project-containers/${id}/company/contacts/${contactId}/email`,
    companyContactDisabled: (id: number, contactId: number) =>
      `/api/project-containers/${id}/company/contacts/${contactId}/disabled`,
    companyBranchOffice: (id: number) => `/api/project-containers/${id}/company/branch-office`,
    companyAdditionalContacts: (id: number) => `/api/project-containers/${id}/company/contacts/additional`,
    companyTravellerContacts: (id: number) => `/api/project-containers/${id}/company/contacts/travellers`,
    contracts: (id: number) => `/api/project-containers/${id}/contracts`,
    contractDetail: (id: number, contractId: number) => `/api/project-containers/${id}/contracts/${contractId}`,
    contractTemplates: (id: number, partyType: number) =>
      `/api/project-containers/${id}/contracts/templates?partyType=${partyType}`,
    contractsFromTemplates: (id: number) => `/api/project-containers/${id}/contracts/from-templates`,
    contractCreateManual: (id: number) => `/api/project-containers/${id}/contracts/upload`,
    contractGeneratePdf: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/generate-pdf`,
    contractUploadPdf: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/upload-pdf`,
    contractSend: (id: number, contractId: number) => `/api/project-containers/${id}/contracts/${contractId}/send`,
    contractPreviewSenderStamp: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/preview-sender-stamp`,
    contractPreviewEmail: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/preview-email`,
    contractCancel: (id: number, contractId: number) => `/api/project-containers/${id}/contracts/${contractId}/cancel`,
    contractMarkSigned: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/mark-signed`,
    contractUpdate: (id: number, contractId: number) => `/api/project-containers/${id}/contracts/${contractId}`,
    contractRemove: (id: number, contractId: number) => `/api/project-containers/${id}/contracts/${contractId}`,
    contractChangeTemplate: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/template`,
    contractResyncEmpty: (id: number, contractId: number) =>
      `/api/project-containers/${id}/contracts/${contractId}/resync-empty`,
    bookings: (id: number, projectHotelId?: number) =>
      projectHotelId == null
        ? `/api/project-containers/${id}/bookings`
        : `/api/project-containers/${id}/bookings?projectHotelId=${projectHotelId}`,
    bookingsAttach: (id: number) => `/api/project-containers/${id}/bookings/attach`,
    booking: (id: number, projectBookingId: number) => `/api/project-containers/${id}/bookings/${projectBookingId}`,
    bookingAvailablePayments: (id: number, companyDivisionId?: number) =>
      companyDivisionId == null
        ? `/api/project-containers/${id}/bookings/available-payments`
        : `/api/project-containers/${id}/bookings/available-payments?companyDivisionId=${companyDivisionId}`,
    cards: (id: number) => `/api/project-containers/${id}/cards`,
    advanceToOngoing: (id: number) => `/api/project-containers/${id}/advance-to-ongoing`,
    assessment: (id: number) => `/api/project-containers/${id}/assessment`,
    completeAssessment: (id: number) => `/api/project-containers/${id}/assessment/complete`,
    cancelAssessment: (id: number) => `/api/project-containers/${id}/assessment/cancel`,
    assessmentNotificationPreview: (id: number) => `/api/project-containers/${id}/assessment/notification/preview`,
    startAssessmentRevision: (id: number) => `/api/project-containers/${id}/assessment/revisions/start`,
    currentAssessmentRevision: (id: number) => `/api/project-containers/${id}/assessment/revisions/current`,
    currentAssessmentRevisionImpact: (id: number) =>
      `/api/project-containers/${id}/assessment/revisions/current/impact`,
    applyCurrentAssessmentRevision: (id: number) => `/api/project-containers/${id}/assessment/revisions/current/apply`,
    cancelCurrentAssessmentRevision: (id: number) =>
      `/api/project-containers/${id}/assessment/revisions/current/cancel`,
    filteredListPost: (page: number, pageSize: number, orderBy: string | undefined, isDesc: boolean | undefined) => {
      let toReturn = `/api/project-containers/filter?page=${page}&pageSize=${pageSize}`;
      if (orderBy !== undefined) {
        toReturn += `&orderBy=${orderBy}`;
      }
      if (isDesc !== undefined) {
        toReturn += `&isDesc=${isDesc}`;
      }
      return toReturn;
    }
  },
  projectDashboard: {
    myContainers: '/api/project-dashboard/my-containers',
    unreadNotAssigned: '/api/project-dashboard/unread-not-assigned'
  },
  projectFolders: {
    create: '/api/project-folders',
    edit: (folderId: number) => `/api/project-folders/${folderId}`
  },
  projectHotels: {
    base: (containerId: number) => `/api/project-containers/${containerId}/hotels`,
    assessmentContext: (containerId: number) => `/api/project-containers/${containerId}/hotels/assessment-context`,
    hotel: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}`,
    archiveHotel: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/archive`,
    disableHotel: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/disable`,
    enableHotel: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/enable`,
    removeHotel: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}`,
    hotelTaxCommission: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/tax-commission`,
    hotelCatalogTaxes: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/catalog-taxes`,
    hotelRoomTypes: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/room-types`,
    hotelEmail: (containerId: number, projectHotelId: number) =>
      `/api/project-containers/${containerId}/hotels/hotels/${projectHotelId}/email`,
    roomType: (containerId: number, projectHotelRoomTypeId: number) =>
      `/api/project-containers/${containerId}/hotels/room-types/${projectHotelRoomTypeId}`,
    archiveRoomType: (containerId: number, projectHotelRoomTypeId: number) =>
      `/api/project-containers/${containerId}/hotels/room-types/${projectHotelRoomTypeId}/archive`,
    unarchiveRoomType: (containerId: number, projectHotelRoomTypeId: number) =>
      `/api/project-containers/${containerId}/hotels/room-types/${projectHotelRoomTypeId}/unarchive`,
    removeRoomType: (containerId: number, projectHotelRoomTypeId: number) =>
      `/api/project-containers/${containerId}/hotels/room-types/${projectHotelRoomTypeId}`,
    roomTypeRates: (containerId: number, projectHotelRoomTypeId: number) =>
      `/api/project-containers/${containerId}/hotels/room-types/${projectHotelRoomTypeId}/rates`,
    rate: (containerId: number, projectHotelRateId: number) =>
      `/api/project-containers/${containerId}/hotels/rates/${projectHotelRateId}`,
    archiveRate: (containerId: number, projectHotelRateId: number) =>
      `/api/project-containers/${containerId}/hotels/rates/${projectHotelRateId}/archive`,
    unarchiveRate: (containerId: number, projectHotelRateId: number) =>
      `/api/project-containers/${containerId}/hotels/rates/${projectHotelRateId}/unarchive`,
    removeRate: (containerId: number, projectHotelRateId: number) =>
      `/api/project-containers/${containerId}/hotels/rates/${projectHotelRateId}`,
    mapCities: (containerId: number, provinceId: number) =>
      `/api/project-containers/${containerId}/hotels/map/cities?provinceId=${provinceId}`,
    mapHotels: (containerId: number, cityId: number, radiusKm: number = 50) =>
      `/api/project-containers/${containerId}/hotels/map/hotels?cityId=${cityId}&radiusKm=${radiusKm}`
  },
  projectIntake: {
    inbox: '/api/project-intake/inbox',
    quickAdd: '/api/project-intake/quickAdd',
    detail: (intakeId: number) => `/api/project-intake/${intakeId}`,
    update: (intakeId: number) => `/api/project-intake/${intakeId}`,
    utility: {
      assignToContainer: (projectId: number) => `/api/project-intake/${projectId}/utility/assignToContainer`,
      delete: (projectId: number) => `/api/project-intake/${projectId}/utility/delete`,
      merge: (projectId: number) => `/api/project-intake/${projectId}/utility/merge`,
      search: (projectId: number) => `/api/project-intake/${projectId}/utility/search`,
      send: (projectId: number) => `/api/project-intake/${projectId}/utility/send`,
      emailBody: (projectId: number) => `/api/project-intake/${projectId}/utility/email-body`
    }
  },
  projectSections: {
    section: (containerId: number, sectionType: ProjectSectionType) =>
      `/api/project-sections/${containerId}/${sectionType}`,
    homeFeed: (containerId: number) => `/api/project-sections/${containerId}/home`,
    create: (containerId: number) => `/api/project-sections/${containerId}/createIntake`,
    remove: (intakeId: number) => `/api/project-sections/${intakeId}/removeIntake`,
    move: (intakeId: number) => `/api/project-sections/${intakeId}/moveIntake`,
    updateIntake: (intakeId: number) => `/api/project-sections/${intakeId}/updateIntake`,
    markIntakeRead: (intakeId: number) => `/api/project-sections/${intakeId}/mark-intake-read`
  }
};
